import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { ApiError, listSyncJobs } from '../api';
import { useSessionUser } from '../state/session';
import type { SyncJobHistoryRow } from '../types';

/**
 * Admin-only page listing recent sync jobs (spec §14 Phase 4).
 *
 * Auth is enforced server-side via `Depends(auth.require_admin)` on
 * /api/sync/jobs; here we additionally hide the page from non-admin users
 * so the route doesn't 403-flash.
 */
export function AdminSyncHistory() {
  const user = useSessionUser();
  const isAdmin = user.role === 'admin' || user.role === 'superuser';

  const [rows, setRows] = useState<SyncJobHistoryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAdmin) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    listSyncJobs(50)
      .then((data) => {
        if (!cancelled) {
          setRows(data);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof ApiError ? e.message : (e as Error)?.message ?? String(e);
        setError(msg);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  if (!isAdmin) {
    return (
      <Shell>
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-sm rounded-md px-4 py-3">
          This page is restricted to administrators.
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Sync history</h1>
          <p className="text-sm text-slate-500">
            Last {rows?.length ?? 0} sync jobs (most recent first).
          </p>
        </div>
        <Link to="/" className="text-sm text-brand-danone hover:underline">
          ← Back to streams
        </Link>
      </header>

      {error && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-md px-4 py-3">
          Couldn&apos;t load sync history: {error}
        </div>
      )}
      {loading && !rows && <div className="text-slate-500">Loading…</div>}
      {rows && rows.length === 0 && (
        <div className="text-sm text-slate-500 italic">No sync runs yet.</div>
      )}
      {rows && rows.length > 0 && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <Th>Started</Th>
                <Th>Status</Th>
                <Th>Duration</Th>
                <Th>By</Th>
                <Th>Records</Th>
                <Th>Error</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <Row key={r.id} row={r} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Shell>
  );
}

function Row({ row }: { row: SyncJobHistoryRow }) {
  const totalRecords = Object.values(row.counts ?? {}).reduce(
    (acc, n) => acc + (typeof n === 'number' ? n : 0),
    0,
  );
  const durationMs = row.finished_at
    ? new Date(row.finished_at).getTime() - new Date(row.started_at).getTime()
    : null;
  return (
    <tr className="align-top">
      <Td>
        <div className="font-mono text-xs text-slate-700">{formatTs(row.started_at)}</div>
        <div className="font-mono text-[10px] text-slate-400 truncate max-w-[12rem]" title={row.id}>
          {row.id}
        </div>
      </Td>
      <Td>
        <StatusPill status={row.status} />
      </Td>
      <Td className="font-mono text-xs">{formatDuration(durationMs)}</Td>
      <Td>
        {row.triggered_by_name ? (
          <>
            <div>{row.triggered_by_name}</div>
            <div className="text-[11px] text-slate-400">{row.triggered_by_email}</div>
          </>
        ) : (
          <span className="text-slate-400 italic">unknown</span>
        )}
      </Td>
      <Td className="font-mono text-xs text-slate-700">
        {totalRecords > 0 ? totalRecords.toLocaleString() : '—'}
      </Td>
      <Td>
        {row.error ? (
          <span className="text-rose-700 break-words">{row.error}</span>
        ) : (
          <span className="text-slate-300">—</span>
        )}
      </Td>
    </tr>
  );
}

function StatusPill({ status }: { status: SyncJobHistoryRow['status'] }) {
  const cls =
    status === 'completed'
      ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
      : status === 'failed'
      ? 'bg-rose-50 text-rose-700 border-rose-200'
      : 'bg-amber-50 text-amber-700 border-amber-200';
  return (
    <span
      className={`inline-flex items-center text-[11px] uppercase tracking-wide font-semibold rounded px-1.5 py-0.5 border ${cls}`}
    >
      {status}
    </span>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="text-left text-xs uppercase tracking-wide font-semibold px-4 py-2">
      {children}
    </th>
  );
}

function Td({
  children,
  className = '',
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return <td className={`px-4 py-2 align-top ${className}`}>{children}</td>;
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
  );
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatDuration(ms: number | null): string {
  if (ms === null || ms < 0) return 'running…';
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}m${r.toString().padStart(2, '0')}s`;
}
